import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { FacilityDashboardPage } from '@sbv-clients/feature-dashboard';

import PrivateRouteLayout from '../layouts/private-route-layout';
import PublicRouteLayout from '../layouts/public-route-layout';
import PrivateRouteGuard from '../route-guards/private-route-guard';
import PublicRouteGuard from '../route-guards/public-route-guard';

const SignIn = lazy(() => import('../pages/sign-in'));
const VerifyPhoneNumber = lazy(() => import('../pages/verify-phone-number'));
const Register = lazy(() => import('../pages/register'));
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy'));
const ResetPassword = lazy(() => import('../pages/reset-password'));
const AuthSuccess = lazy(() => import('../pages/auth-success'));
const OpenShifts = lazy(() => import('../pages/openshifts'));
const SbvPro = lazy(() => import('../pages/sbv-pro'));
const ContactUs = lazy(() => import('../pages/contact-us'));
const Chat = lazy(() => import('../pages/chat'));
const Home = lazy(() => import('../pages/home'));
const Dashboard = lazy(() => import('../pages/dashboard'));
const TimeCards = lazy(() => import('../pages/time-cards'));
const ClockCommunication = lazy(() => import('../pages/clock-communication'));
const StaticReports = lazy(() => import('../pages/static-reports'));
const PayrollExports = lazy(() => import('../pages/payroll-exports'));
const DynamicReport = lazy(() => import('../pages/dynamic-report'));
const Scheduler = lazy(() => import('../pages/scheduler'));
const SchedulerDailyView = lazy(() => import('../pages/scheduler/daily-view'));
const SchedulerDailyViewByUnit = lazy(
  () => import('../pages/scheduler/daily-view-by-unit')
);
const Positions = lazy(() => import('../pages/scheduler/positions/list'));
const ProLaborLevelDesignations = lazy(
  () => import('../pages/scheduler/pro-designations/list')
);
const Departments = lazy(() => import('../pages/scheduler/departments/list'));
const Units = lazy(() => import('../pages/scheduler/units/list'));
const Roles = lazy(() => import('../pages/roles'));
const AbsenceReport = lazy(() => import('../pages/absence-report'));
const PrintMasterSchedule = lazy(
  () => import('../pages/print-master-schedule')
);
const PrintMonthlySchedule = lazy(
  () => import('../pages/print-monthly-schedule')
);
const PrintAvailableSchedules = lazy(
  () => import('../pages/print-available-schedules')
);
const PrintStaffingSheetPage = lazy(
  () => import('../pages/print-staffing-sheet')
);
const MasterSchedule = lazy(() => import('../pages/master-schedule'));
const ShiftTypes = lazy(() => import('../pages/scheduler/shift-types/list'));
const Facilities = lazy(() => import('../pages/scheduler/facilities/list'));
const OpeningTemplates = lazy(
  () => import('../pages/scheduler/opening-templates/list')
);
const Company = lazy(() => import('../pages/scheduler/company/list'));
const ImportsPage = lazy(() => import('../pages/scheduler/imports-page'));
const ToolsPage = lazy(() => import('../pages/tools'));
const ProcessingPage = lazy(() => import('../pages/tools/processing'));
const ClosePayPeriodPage = lazy(
  () => import('../pages/tools/close-pay-period')
);
const ToolsImportPage = lazy(() => import('../pages/tools/imports'));
const ListCensusPage = lazy(() => import('../pages/census'));
const ListAbsenceReasonPage = lazy(() => import('../pages/absence-reason'));

const FacilityDashboard = lazy(() => import('../pages/facility-dashboard'));
const FacilityStatsPage = lazy(() => import('../pages/stats'));
const WebPunch = lazy(() => import('../pages/web-punch'));
const Preferences = lazy(() => import('../pages/preferences'));
const Page404 = lazy(() => import('../pages/page404'));

const getPage404Config = () => {
  return {
    path: '*',
    element: (
      <Suspense>
        <Page404 />
      </Suspense>
    ),
  };
};
const EmployeeSchedulerPage = lazy(() => import('../pages/employee-scheduler'));

const redirectAuthRoute = '/auth/verify-phone';

const routesConfig: RouteObject[] = [
  {
    path: '/',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <FacilityDashboardPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Dashboard />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'privacy-policy',
    element: (
      <PublicRouteLayout>
        <PrivateRouteLayout />
      </PublicRouteLayout>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'sbv-pro',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <SbvPro />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'contact-us',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ContactUs />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'auth',
    element: <PublicRouteLayout />,
    children: [
      {
        path: 'sign-in',
        element: (
          <PublicRouteGuard>
            <Suspense>
              <SignIn />
            </Suspense>
          </PublicRouteGuard>
        ),
      },
      {
        path: 'verify-phone',
        element: (
          <PublicRouteGuard>
            <Suspense>
              <VerifyPhoneNumber />
            </Suspense>
          </PublicRouteGuard>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <PublicRouteGuard>
            <Suspense>
              <ResetPassword />
            </Suspense>
          </PublicRouteGuard>
        ),
      },
      {
        path: 'register',
        element: (
          <PublicRouteGuard>
            <Suspense>
              <Register />
            </Suspense>
          </PublicRouteGuard>
        ),
      },
      {
        path: 'success',
        element: (
          <PublicRouteGuard>
            <Suspense>
              <AuthSuccess />
            </Suspense>
          </PublicRouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={redirectAuthRoute} replace />,
      },
      getPage404Config(),
    ],
  },
  {
    path: 'openshifts',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <OpenShifts />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'chat',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Chat />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'employees/*',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <TimeCards />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense>
            <TimeCards />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'preferences',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Preferences />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'clock-communication',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ClockCommunication />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'static-reports',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="employees" replace />,
      },
      {
        path: 'employees',
        element: (
          <Suspense>
            <StaticReports />
          </Suspense>
        ),
      },
      {
        path: 'schedules',
        element: (
          <Suspense>
            <StaticReports />
          </Suspense>
        ),
      },
      {
        path: 'employees/:reportType/:mediaType',
        element: (
          <Suspense>
            <DynamicReport />
          </Suspense>
        ),
      },
      {
        path: 'schedules/absence-reports',
        element: (
          <Suspense>
            <AbsenceReport />
          </Suspense>
        ),
      },
      {
        path: 'schedules/print-available-schedules',
        element: (
          <Suspense>
            <PrintAvailableSchedules />
          </Suspense>
        ),
      },
      {
        path: 'schedules/print-master-schedule',
        element: (
          <Suspense>
            <PrintMasterSchedule />
          </Suspense>
        ),
      },
      {
        path: 'schedules/print-monthly-schedule',
        element: (
          <Suspense>
            <PrintMonthlySchedule />
          </Suspense>
        ),
      },
      {
        path: 'schedules/print-staffing-sheet',
        element: (
          <Suspense>
            <PrintStaffingSheetPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'payroll-exports',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <PayrollExports />
          </Suspense>
        ),
      },
      {
        path: ':reportType/:mediaType',
        element: (
          <Suspense>
            <DynamicReport />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'master-schedule-planner',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <MasterSchedule />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'scheduler',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Scheduler />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'daily-view',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <SchedulerDailyView />
          </Suspense>
        ),
      },
      {
        path: 'unit',
        element: (
          <Suspense>
            <SchedulerDailyViewByUnit />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'by-unit-daily-view',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <SchedulerDailyViewByUnit />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'departments',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Departments />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'positions',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Positions />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'pro-designations',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ProLaborLevelDesignations />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'units',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Units />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'roles',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Roles />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'facilities',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Facilities />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'shift-types',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ShiftTypes />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'opening-templates',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <OpeningTemplates />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'company',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Company />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'census',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ListCensusPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'absence-reason',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ListAbsenceReasonPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'facility-dashboard',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <FacilityDashboard />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'stats',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <FacilityStatsPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'employee-scheduler',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <EmployeeSchedulerPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'web-punch',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <WebPunch />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'imports',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ImportsPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: 'tools',
    element: (
      <PrivateRouteGuard>
        <PrivateRouteLayout />
      </PrivateRouteGuard>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <ToolsPage />
          </Suspense>
        ),
      },
      {
        path: 'processing',
        element: (
          <Suspense>
            <ProcessingPage />
          </Suspense>
        ),
      },
      {
        path: 'close-pay-period',
        element: (
          <Suspense>
            <ClosePayPeriodPage />
          </Suspense>
        ),
      },
      {
        path: 'clock-communication',
        element: (
          <Suspense>
            <ClockCommunication />
          </Suspense>
        ),
      },
      {
        path: 'imports',
        element: (
          <Suspense>
            <ToolsImportPage />
          </Suspense>
        ),
      },
      getPage404Config(),
    ],
  },
  {
    path: '/verification/*',
    element: <Navigate to={redirectAuthRoute} replace />,
  },
  getPage404Config(),
];

export default routesConfig;
